import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import logoSrc from '../assets/logo_1000ad.png';

const BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * BrandLogo — renders the tenant's custom brand logo if uploaded,
 * otherwise falls back to the 1000AD logo asset.
 * Cache-busting via ?v=<brand_logo_updated_at>.
 *
 * @param {number}  size       Height in px (width auto for text logo, square for brand)
 * @param {string}  className  Extra classes
 * @param {boolean} rounded    Add rounded corners (for square brand logos)
 * @param {'dark'|'light'} theme  Controls fallback filter (auto-detected from data-theme if omitted)
 * @param {string}  sourceUrl  Direct URL to a logo — bypasses tenant context (for pre-auth branded login)
 */
export default function BrandLogo({ size = 48, className = '', rounded = false, theme, sourceUrl }) {
  const { tenant } = useAuth();
  const { brand } = useTenant();
  const [err, setErr] = useState(false);

  // Allow direct URL override for pre-auth use (e.g. branded login page)
  if (sourceUrl && !err) {
    return (
      <img
        src={sourceUrl}
        alt="Logo"
        data-testid="brand-logo"
        className={`object-contain ${rounded ? 'rounded-xl' : ''} ${className}`}
        style={{ width: size, height: size }}
        onError={() => setErr(true)}
        draggable={false}
      />
    );
  }

  const hasBrand = brand?.brand_logo_url && tenant?.id && !err;

  if (hasBrand) {
    const v = brand.brand_logo_updated_at || '';
    const src = `${BASE}/api/brand/${tenant.id}/logo${v ? `?v=${encodeURIComponent(v)}` : ''}`;
    return (
      <img
        src={src}
        alt={tenant?.name || 'Logo'}
        data-testid="brand-logo"
        className={`object-contain ${rounded ? 'rounded-xl' : ''} ${className}`}
        style={{ width: size, height: size }}
        onError={() => setErr(true)}
        draggable={false}
      />
    );
  }

  // Fallback: 1000AD logo — adapt filter for light vs dark
  // Auto-detect from nearest data-theme ancestor if not explicitly provided
  const resolvedTheme = theme || 'light';
  const isDark = resolvedTheme === 'dark';
  return (
    <img
      src={logoSrc}
      alt="1000AD"
      data-testid="brand-logo-default"
      className={`object-contain ${className}`}
      style={{
        height: size,
        width: 'auto',
        filter: isDark
          ? 'drop-shadow(0 0 6px rgba(255,255,255,0.5)) drop-shadow(0 0 18px rgba(139,92,246,0.3))'
          : 'brightness(0.2) contrast(1.2)',
      }}
      draggable={false}
    />
  );
}
